/**
 * useRealtimeInvalidation
 *
 * Subscribes to the WebSocket real-time layer and automatically
 * invalidates React Query caches when relevant model events arrive.
 *
 * Mount this once near the root (e.g. in AppLayout or a top-level provider).
 */

import { useQueryClient } from '@tanstack/react-query';
import { useRealtimeSocket, type RealtimeMessage } from './useRealtimeSocket';

const MODEL_TO_QUERY_KEYS: Record<string, string[][]> = {
  // ── Core models ──────────────────────────────────────────────────────
  Booking: [['bookings'], ['bookingDetail']],
  Room: [['rooms']],
  RoomAvailability: [['rooms']],
  // A guest-level change (name/email/phone, or a BL GuestUpdated that moves
  // the booking financials) must also refresh the open booking detail page:
  // its per-guest enrichment lives under ['bookingGuests'] and its check-in/
  // financial mirror under ['bookingDetail']. Without these keys a bare Guest
  // event only refreshed the standalone guest list, leaving the booking page
  // stale until a manual refetch.
  Guest: [['guests'], ['bookingGuests'], ['bookingDetail']],
  Notification: [['notifications']],

  // ── Treasury models ──────────────────────────────────────────────────
  // Keys must match the QUERY_KEY constants in useTreasury, useBankAccounts,
  // useBankTransactions, and useTreasuryDomain hooks.
  FinancialOperation: [['treasury-transactions'], ['treasury-summary'], ['treasury-category-summary']],
  BankAccount: [['treasury-bank-accounts']],
  BankTransaction: [['bank-transactions'], ['reconciliation-matches']],
  BankProviderConnection: [['bankProviderConnection']],
  PendingObligation: [['treasury-pending-obligations']],

  // ── POS models ───────────────────────────────────────────────────────
  // orderKeys.all = ['orders'] in usePOS.ts
  POSOrder: [['orders']],
  POSOrderItem: [['orders']],

  // ── Activities ───────────────────────────────────────────────────────
  Activity: [['activities']],
  // enrollmentKeys.all = ['activity-enrollments'] in useEnrollments.ts
  ActivityEnrollment: [['activity-enrollments']],

  // ── Staff ────────────────────────────────────────────────────────────
  // staffKeys.all = ['staff'] in useStaff.ts
  Staff: [['staff']],
  // staffExpenseKeys.all = ['staff-expenses'] in useStaffExpenses.ts
  StaffExpense: [['staff-expenses']],
  // staffTokenKeys.all = ['staff-tokens'] in useStaffTokens.ts
  StaffTokenBalance: [['staff-tokens']],

  // ── Suppliers ────────────────────────────────────────────────────────
  // QUERY_KEY = 'suppliers' in useSuppliers.ts
  Supplier: [['suppliers']],
  // QUERY_KEY = 'purchase-orders' in usePurchaseOrders.ts
  PurchaseOrder: [['purchase-orders']],

  // ── Prepaid ──────────────────────────────────────────────────────────
  // guestPrepaidKeys.all = ['guest-prepaid'] in useGuestPrepaid.ts
  GuestPrepaidBalance: [['guest-prepaid']],
  GuestPrepaidTransaction: [['guest-prepaid']],

  // ── Integrations ─────────────────────────────────────────────────────
  // BL sync progress. Backend emits this on every job.save() during a
  // Bookinglayer sync; the invalidation triggers an immediate useSyncJobs
  // refetch so the progress UI updates without waiting for the next
  // polling tick.
  BookinglayerSyncJob: [['bookinglayer', 'syncJobs'], ['bookinglayer', 'status']],
};

export function useRealtimeInvalidation() {
  const queryClient = useQueryClient();

  useRealtimeSocket(
    (msg: RealtimeMessage) => {
      if (msg.type === 'pong') return;

      // D1: Special handler for sync_completed event
      if (msg.type === 'sync_completed') {
        const affectedQueries = (msg.data?.affected_queries as string[]) || ['bookings', 'rooms', 'guests'];
        affectedQueries.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey: [queryKey], exact: false });
        });
        // The sync-progress panel reads its own queries; without these the
        // finished job stays frozen mid-phase (polling stops on the last
        // 'running' snapshot and no model event may follow).
        queryClient.invalidateQueries({ queryKey: ['bookinglayer', 'syncJobs'], exact: false });
        queryClient.invalidateQueries({ queryKey: ['bookinglayer', 'status'], exact: false });
        return;
      }

      // Support events: emitted by apps/support/tasks.py on the
      // submitter's per-tenant `user_{schema}_{user_id}` channel when
      // a Shaka admin replies. Invalidate the user's ticket list and
      // the open detail so the new message appears without manual
      // refresh of the Help & Support page.
      if (msg.type === 'support_event') {
        queryClient.invalidateQueries({ queryKey: ['support', 'tickets'], exact: false });
        const ticketId = (msg as { ticket_id?: string }).ticket_id;
        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: ['support', 'tickets', ticketId] });
        }
        return;
      }

      const model = msg.model;
      if (!model) return;

      const keys = MODEL_TO_QUERY_KEYS[model];
      if (keys) {
        // Use prefix matching for keys that fan out across many sub-queries
        // (rooms, treasury, bookinglayer, orders, staff, etc.) so a single
        // event invalidates the list, detail, and summary variants.
        keys.forEach(key => {
          queryClient.invalidateQueries({
            queryKey: key,
            exact: false,
          });
        });
      }

      // Also invalidate the specific record if we have its ID
      if (msg.id && model === 'Booking') {
        queryClient.invalidateQueries({ queryKey: ['bookingDetail', msg.id] });
      }
      if (msg.id && model === 'Room') {
        queryClient.invalidateQueries({ queryKey: ['rooms', 'detail', msg.id] });
      }
    },
    {
      onReconnect: () => {
        // D5: Refetch only stale queries on reconnect, not all (prevents thunder herd)
        queryClient.invalidateQueries({ stale: true });
      },
    },
  );
}
